/**
 * MapTab — the map tab UI with search, filters, sort, floor selector,
 * interactive legend SVG, zoomable map, route list, and setter FAB.
 * Also: a spray wall legend row and a spray wall branch of the placement banner.
 */
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Pill, Segmented, DifficultyFilterPills, IconClose, IconBtn, IconTarget, ShowMoreBtn, SELECTABLE, EmptyState, ViewportLayer, SprayWallMarkerGlyph,
  FilterModal, FilterSection, FilterChips, NewRouteFab, NEW_ROUTE_FAB_CLEARANCE,
} from "./ui";
import { isClimbEntry } from "@sendly/shared";
import { visibleMapWalls } from "../utils/sprayWallMap";
import { QuickLogBar } from "./QuickLogBar";
import { ZoomableMap } from "./ZoomableMap";
import { resolveMapImageUrl, formatPoints } from "../utils";
import { useTranslation, tDiffLabel } from "../i18n";
import { F, resolveNewRouteDays, ROUTE_LIST_PREVIEW } from "../constants";
import { isMixedGym } from "../utils/disciplineUtils";
import { logEvent, EVT } from "../analytics";
import { getRouteDisplayName, getSetterDisplayName } from "../utils/routeDisplayUtils";
import { useExpanderState } from "../hooks/useExpanderState";
import { useDateKey } from "../hooks/useDateKey";
import { useAppTheme } from "../contexts/ThemeContext";

// ROUTE_LIST_PREVIEW (constants.js): the rows the route list shows at first
// and adds per "Továbbiak mutatása" tap. The list is the only place a phone
// user can read route names, grades and setters without hunting markers.

export const MapTab = React.memo(function MapTab({
  // theme & colors
 diff, holdC,
  // data
  routes, filteredRoutes, sortedRouteList, doneSet, compToppedRouteIds, avgRatingMap,
  // map state
  maps, currentMapId, currentMap,
  activeMapId, setActiveMapId,
  // search & filter
  mapSearch, setMapSearch, filters, setFilters,
  showMapFilters, setShowMapFilters,
  activeFilterCount, toggleDiff, hasUngradedRoutes, hasUnsetFieldRoutes,
  // sort (rendered inside the filter modal)
  mapSort, setMapSort,
  // (showLegend is local + persisted; the route list's visible count is local only)
  // route placement (routes.create)
  canCreateRoute, pending, setPending, setShowBuilder,
  // route selection
  setSelRoute, onRouteLongPress,
  // quick log (one tap = one climb, no modal)
  quickLog, archiveMode, canArchive = false, todayClimbLog,
  // map interaction
  handleMapClickOrReposition,
  // competition
  hasActiveComp,
  // discipline
  gymMeta,
  disciplineFilter, setDisciplineFilter,
  // legend onboarding (per-user, persisted in Firestore)
  userData, onMarkLegendSeen,
  // gym settings (admin-configured: new-route highlight threshold, etc.)
  settings,
  // One-shot focus request from the routes tab → ZoomableMap zooms to and
  // pulses the marker for the requested route. Object identity changes on
  // every request (carries a timestamp) so repeat clicks on the same route
  // also fire.
  mapFocusRequest,
  // Handler shared with the routes tab — when the route list under the
  // map gets its own target icons, they fire this same callback so the
  // user can locate routes from either list with one mental model.
  onShowOnMap,
  compRouteIds,
  // Spray wall markers (add-on gyms only; the list is already add-on gated),
  // marker tap → open the wall, and the spraywall.manage gate for placement.
  sprayWalls = [], onSprayWallClick = null, canManageSprayWall = false,
}) {
  const { accent, th } = useAppTheme();
  const { t, lang } = useTranslation();
  const gymScope = gymMeta?.id;
  // Gym-tz "today" so the "new route" window matches route.createdAt (written in
  // the gym timezone), not UTC — shared with the TV dashboard's newRouteCutoff.
  const today = useDateKey(gymMeta?.timezone);
  // Legend auto-opens exactly once per user account (flag lives on the user
  // doc in Firestore, so it carries across all devices/browsers).
  // Quick log: a marker tap LOGS instead of opening the route. `tapRoute`
  // reports whether it consumed the tap, so turning the mode off restores the
  // normal behaviour with no branching here.
  const handleRouteTap = useCallback(async (route) => {
    // Archive mode is checked first: it is fullscreen-only and the two modes
    // disarm each other where they are toggled, so both can never be on — but
    // if a future path ever armed both, the destructive one must not be the
    // silent loser.
    if (archiveMode?.active) {
      const consumed = await archiveMode.tapRoute(route);
      if (consumed) return;
    }
    if (quickLog?.active) {
      const consumed = await quickLog.tapRoute(route);
      if (consumed) return;
    }
    setSelRoute(route);
  }, [archiveMode, quickLog, setSelRoute]);

  // routeId → how many times it was climbed TODAY, for the marker's ×N pill.
  // Bottom-right of the marker: top-right is the climbed check, top-left the
  // competition badge. A project-day `attempt` entry is not a climb (U8/U9:
  // the quick log has no tries and the marker no project badge).
  const quickLogCounts = useMemo(() => {
    if (!quickLog?.active) return null;
    const out = {};
    for (const c of Object.values(todayClimbLog?.climbs || {})) {
      if (c && c.routeId && isClimbEntry(c)) out[c.routeId] = (out[c.routeId] || 0) + 1;
    }
    return out;
  }, [quickLog?.active, todayClimbLog]);

  // Placing a spray wall reuses the route placement `pending` state with kind
  // "sprayWall"; only live walls of this floor render as markers.
  const placingWall = pending?.kind === "sprayWall";
  const mapSprayWalls = useMemo(() => visibleMapWalls(sprayWalls, currentMapId), [sprayWalls, currentMapId]);

  const [showLegend, toggleLegend, setShowLegend] = useExpanderState("map.legend", false);
  // Route list pagination — one batch at a time, revealed from the FOOTER of
  // the list (see ShowMoreBtn). A header-side "show all" told the user about
  // the cut on a line they had already scrolled past, so a truncated list read
  // as the whole wall. Reset on every input that reshapes the list, otherwise
  // a narrowed result inherits a count that hides nothing.
  const [routeListVisible, setRouteListVisible] = useState(ROUTE_LIST_PREVIEW);
  useEffect(() => { setRouteListVisible(ROUTE_LIST_PREVIEW); },
    [mapSearch, filters, mapSort, currentMapId, disciplineFilter]);
  const legendPrimedRef = useRef(false);
  useEffect(() => {
    if (legendPrimedRef.current) return;
    if (!userData) return; // wait for user doc to load
    legendPrimedRef.current = true;
    if (!userData.legendSeen) {
      setShowLegend(true);
      onMarkLegendSeen?.();
    }
  }, [userData, setShowLegend, onMarkLegendSeen]);

  // Track map view once per gym per mount cycle
  const mapViewLogged = useRef(false);
  useEffect(() => {
    const gid = gymMeta?.id;
    if (gid && !mapViewLogged.current) {
      mapViewLogged.current = true;
      logEvent(EVT.MAP_VIEW, { gym_id: gid, floor_count: maps?.length || 1 });
    }
  }, [gymMeta?.id]);

  return (
    // Bottom padding reserves room for the fixed "new route" FAB (≈44px tall +
    // 20px bottom offset) plus the iOS home-indicator inset, so the last route
    // row is never hidden behind the floating button on a fully-scrolled list.
    <div style={{ paddingBottom: NEW_ROUTE_FAB_CLEARANCE }}>
      {/* Animations */}
      <style>{`
      `}</style>

      {/* Placement banner — shown while a route is waiting for its map tap. */}
      {pending && (placingWall ? canManageSprayWall : canCreateRoute) && <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", background: accent + "18", border: "1px solid " + accent + "44", borderRadius: 14, fontSize: 13, color: accent, margin: "12px 12px 0" }}>
        <span>{placingWall
          ? t('map.clickToPlaceSprayWall', { name: pending.name })
          : (pending._editId ? t('map.clickToReposition') : t('map.clickToPlace', { name: pending.name }))}</span>
        <IconBtn icon={IconClose} tone="default" size={14} padding={4} title={t('common.close')} onClick={() => setPending(null)} style={{ marginLeft: "auto", color: accent }} />
      </div>}

      {/* Search bar is rendered in App.js sticky header block */}

      {/* 2. ACTIVE FILTER CHIPS — chips row, no "Clear all" outside (lives in the sheet) */}
      {activeFilterCount > 0 && (() => {
        const chips = [];
        if (isMixedGym(gymMeta) && disciplineFilter !== "all") {
          chips.push({
            key: "disc",
            label: disciplineFilter === "boulder" ? t('discipline.boulder') : t('discipline.lead'),
            onRemove: () => setDisciplineFilter("all"),
          });
        }
        if (filters.status !== "all") {
          chips.push({
            key: "status",
            label: filters.status === "todo" ? t('map.statusUnclimbed') : t('map.statusClimbed'),
            onRemove: () => setFilters(f => ({ ...f, status: "all" })),
          });
        }
        for (const k of (filters.difficulty || [])) {
          const isUngraded = k === "_ungraded";
          const d = isUngraded ? null : diff[k];
          chips.push({
            key: "diff-" + k,
            dotColor: isUngraded ? null : (d?.color || "#ccc"),
            dotBorder: isUngraded ? null : (d?.border || d?.color || "#999"),
            hatched: isUngraded,
            label: isUngraded ? t('route.ungraded') : tDiffLabel(t, k, d?.label || "?"),
            onRemove: () => setFilters(f => ({ ...f, difficulty: (f.difficulty || []).filter(x => x !== k) })),
          });
        }
        if (filters.compOnly) {
          chips.push({
            key: "comp",
            label: "🏆 " + t('map.filterComp'),
            onRemove: () => setFilters(f => ({ ...f, compOnly: false })),
          });
        }
        return <FilterChips chips={chips} style={{ padding: "4px 12px 12px" }} />;
      })()}

      {/* 3. CENTERED MODAL — filters + sort combined */}
      {showMapFilters && (
        <FilterModal
          title={t('filter.sheetTitle')}
          onClose={() => setShowMapFilters(false)}
          clearLabel={t('filter.clearAll')}
          onClear={activeFilterCount > 0 ? () => {
            setFilters(f => ({ ...f, status: "all", difficulty: [], compOnly: false }));
            if (isMixedGym(gymMeta) && typeof setDisciplineFilter === "function") setDisciplineFilter("all");
          } : null}
          resultCount={sortedRouteList.length}
          onShow={() => setShowMapFilters(false)}
        >
          {isMixedGym(gymMeta) && (
            <FilterSection title={t('discipline.label')}>
              <Segmented
                options={[
                  { value: "all", label: t('discipline.filterAll') },
                  { value: "boulder", label: t('discipline.boulder') },
                  { value: "lead", label: t('discipline.lead') },
                ]}
                value={disciplineFilter}
                onChange={setDisciplineFilter}
                ariaLabel={t('discipline.label')}
              />
            </FilterSection>
          )}
          <FilterSection title={t('map.filterStatus')}>
            <Segmented
              options={[
                { value: "all", label: t('map.statusAll') },
                { value: "todo", label: t('map.statusUnclimbed') },
                { value: "done", label: t('map.statusClimbed') },
              ]}
              value={filters.status}
              onChange={(v) => { setFilters(f => ({ ...f, status: v })); logEvent(EVT.ROUTE_FILTER_USED, { gym_id: gymMeta?.id, filter_type: "status", tab: "map" }); }}
              ariaLabel={t('map.filterStatus')}
            />
          </FilterSection>
          <FilterSection title={t('map.filterDifficulty')}>
            <DifficultyFilterPills
              diff={diff} selected={filters.difficulty} hasUngraded={hasUngradedRoutes}

              onToggle={(k) => {
                setFilters(f => {
                  const cur = f.difficulty;
                  const next = cur.includes(k) ? cur.filter(d => d !== k) : [...cur, k];
                  return { ...f, difficulty: next };
                });
                logEvent(EVT.ROUTE_FILTER_USED, { gym_id: gymMeta?.id, filter_type: "difficulty", tab: "map" });
              }}
            />
          </FilterSection>
          {hasActiveComp && (
            <FilterSection>
              <Pill
                active={filters.compOnly}
                onClick={() => { setFilters(f => ({ ...f, compOnly: !f.compOnly })); logEvent(EVT.ROUTE_FILTER_USED, { gym_id: gymMeta?.id, filter_type: "comp_only", tab: "map" }); }}
              >
                🏆 {t('map.filterComp')}
              </Pill>
            </FilterSection>
          )}
          <FilterSection title={t('filter.sortLabel')}>
            <Segmented
              options={[
                { value: "name", label: t('map.sortName') },
                { value: "diffAsc", label: t('map.sortDiffAsc') },
                { value: "diffDesc", label: t('map.sortDiffDesc') },
                { value: "rating", label: t('map.sortRating') },
                { value: "ascentsDesc", label: t('map.sortAscentsDesc') },
                { value: "climbsDesc", label: t('map.sortClimbsDesc') },
                { value: "newest", label: t('map.sortNewest') },
              ]}
              value={mapSort}
              onChange={(v) => { setMapSort(v); logEvent(EVT.ROUTE_FILTER_USED, { gym_id: gymMeta?.id, filter_type: "sort", tab: "map" }); }}
              wrap
              ariaLabel={t('filter.sortLabel')}
            />
          </FilterSection>
        </FilterModal>
      )}

      {/* 3. MAP TABS (floor/map selector) — segmented */}
      {maps.length > 1 && (
        <div style={{ padding: "0 12px 8px" }}>
          <Segmented
            options={maps.map(m => ({ value: m.id, label: m.name }))}
            value={currentMapId}
            onChange={setActiveMapId}
            scroll
            ariaLabel={t('map.floors') || 'Floors'}
          />
        </div>
      )}

      {/* 4. LEGEND */}
      <div style={{ padding: "0 12px" }}>
        <button type="button" onClick={toggleLegend} aria-expanded={showLegend} style={{
          width: "100%", padding: "10px 0", background: "none", border: "none", cursor: "pointer", fontFamily: F,
          fontSize: 12, color: th.textMuted, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          <span style={{ fontSize: 12 }}>{showLegend ? "\u25B2" : "\u25BC"}</span>
          {showLegend ? t('map.legendHide') : t('map.legendToggle')}
        </button>

        {showLegend && (
          <div style={{ background: th.card, borderRadius: 14, border: "1px solid " + th.border, padding: 16, marginBottom: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <svg viewBox="0 0 400 260" width="100%" style={{ maxWidth: 400 }}>
              {/* New route pulse */}
              <circle cx="200" cy="120" r="28" fill="none" stroke="#2563eb" strokeWidth="2" strokeDasharray="4,3" opacity="0.4" />
              {/* Outer circle - hold color (no stroke, like actual markers) */}
              <circle cx="200" cy="120" r="20" fill="#f97316" opacity="0.9" />
              {/* Inner circle - difficulty */}
              <circle cx="200" cy="120" r="10" fill="#2563eb" stroke="#1d4ed8" strokeWidth="2.5" />
              {/* Points text */}
              <text x="200" y="120.5" textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="10" fontWeight="700" fontFamily="'Space Grotesk',sans-serif">2p</text>
              {/* Climbed badge */}
              <circle cx="216" cy="103" r="8" fill={accent} stroke="#fff" strokeWidth="2" />
              <polyline points="212,104 215,107 220,101" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

              {/* Annotation 1: Hold color - top left */}
              <line x1="184" y1="108" x2="80" y2="44" stroke={th.textMuted} strokeWidth="1.5" strokeDasharray="4,3" />
              <circle cx="184" cy="108" r="3" fill={th.textMuted} />
              <rect x="5" y="22" width="140" height="46" rx="10" fill={th.card} stroke="#f97316" strokeWidth="1.5" />
              <text x="75" y="43" textAnchor="middle" fill={th.text} fontSize="14" fontWeight="700" fontFamily="'Space Grotesk',sans-serif">{t('map.legendHoldColor')}</text>
              <text x="75" y="60" textAnchor="middle" fill={th.textMuted} fontSize="12" fontFamily="'Space Grotesk',sans-serif">{t('map.legendHoldExample')}</text>

              {/* Annotation 2: Climbed badge - top right */}
              <line x1="222" y1="100" x2="300" y2="44" stroke={th.textMuted} strokeWidth="1.5" strokeDasharray="4,3" />
              <circle cx="222" cy="100" r="3" fill={th.textMuted} />
              <rect x="250" y="22" width="135" height="46" rx="10" fill={th.card} stroke={accent} strokeWidth="1.5" />
              <text x="317" y="51" textAnchor="middle" fill={th.text} fontSize="14" fontWeight="700" fontFamily="'Space Grotesk',sans-serif">{t('map.legendClimbed')} ✓</text>

              {/* Annotation 3: Difficulty - right middle */}
              <line x1="206" y1="113" x2="300" y2="119" stroke={th.textMuted} strokeWidth="1.5" strokeDasharray="4,3" />
              <circle cx="206" cy="113" r="3" fill={th.textMuted} />
              <rect x="250" y="97" width="145" height="46" rx="10" fill={th.card} stroke="#2563eb" strokeWidth="1.5" />
              <text x="322" y="118" textAnchor="middle" fill={th.text} fontSize="14" fontWeight="700" fontFamily="'Space Grotesk',sans-serif">{t('map.legendDifficulty')}</text>
              <text x="322" y="135" textAnchor="middle" fill={th.textMuted} fontSize="12" fontFamily="'Space Grotesk',sans-serif">{t('map.legendDiffExample')}</text>

              {/* Annotation 4: New route pulse - bottom left */}
              <line x1="178" y1="138" x2="80" y2="192" stroke={th.textMuted} strokeWidth="1.5" strokeDasharray="4,3" />
              <circle cx="178" cy="138" r="3" fill={th.textMuted} />
              <rect x="5" y="176" width="145" height="46" rx="10" fill={th.card} stroke="#2563eb" strokeWidth="1.5" />
              <text x="77" y="197" textAnchor="middle" fill={th.text} fontSize="14" fontWeight="700" fontFamily="'Space Grotesk',sans-serif">{t('map.legendNewRoute')}</text>
              <text x="77" y="214" textAnchor="middle" fill={th.textMuted} fontSize="12" fontFamily="'Space Grotesk',sans-serif">({t('map.legendPulsing')})</text>

              {/* Annotation 5: Points - bottom right */}
              <line x1="200" y1="130" x2="200" y2="192" stroke={th.textMuted} strokeWidth="1.5" strokeDasharray="4,3" />
              <circle cx="200" cy="130" r="3" fill={th.textMuted} />
              <rect x="155" y="196" width="110" height="36" rx="10" fill={th.card} stroke={th.textMuted} strokeWidth="1.5" />
              <text x="210" y="220" textAnchor="middle" fill={th.text} fontSize="14" fontWeight="700" fontFamily="'Space Grotesk',sans-serif">{t('map.legendPoints')}</text>
            </svg>
            {/* Small marker (zoomed-out view). The center dot is the climbed
                flag, NOT the difficulty — difficulty only appears on the
                zoomed-in marker, which the diagram above shows. */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px" }}>
              <svg width="64" height="40" viewBox="0 0 64 40" style={{ flexShrink: 0 }}>
                <circle cx="16" cy="20" r="13" fill="#f97316" opacity="0.92" />
                <circle cx="48" cy="20" r="13" fill="#f97316" opacity="0.92" />
                <circle cx="48" cy="20" r="5.2" fill={accent} />
              </svg>
              <span style={{ fontSize: 12, color: th.textMuted }}>{t('map.legendSmall')}</span>
            </div>
            {hasUnsetFieldRoutes && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px" }}>
                <svg width="40" height="40" viewBox="0 0 40 40" style={{ flexShrink: 0 }}>
                  <circle cx="20" cy="20" r="14" fill="url(#hatch-legend-unset)" stroke={accent} strokeWidth="1.5" opacity="0.9" />
                  <text x="20" y="20.5" textAnchor="middle" dominantBaseline="middle" fill={accent} fontSize="11" fontWeight="700" fontFamily="'Space Grotesk',sans-serif">?</text>
                  <defs>
                    <pattern id="hatch-legend-unset" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                      <line x1="0" y1="0" x2="0" y2="6" stroke={accent} strokeWidth="1.5" strokeOpacity="0.5" />
                    </pattern>
                  </defs>
                </svg>
                <span style={{ fontSize: 12, color: th.textMuted }}>{t('legend.unsetRoute')}</span>
              </div>
            )}
            {hasActiveComp && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px" }}>
                <svg width="44" height="44" viewBox="0 0 44 44" style={{ flexShrink: 0 }}>
                  <circle cx="22" cy="22" r="17.5" fill="none" stroke="#f59e0b" strokeWidth="2" strokeDasharray="3,2" opacity="0.9" />
                  <circle cx="22" cy="22" r="14" fill="#f97316" opacity="0.9" />
                  <g transform="translate(10, 10)">
                    <circle r="6.5" fill="#f59e0b" stroke="#fff" strokeWidth="1.5" />
                    <text x="0" y="0.5" textAnchor="middle" dominantBaseline="middle" fontSize="7">🏆</text>
                  </g>
                </svg>
                <span style={{ fontSize: 12, color: th.textMuted }}>{t('map.legendComp')}</span>
              </div>
            )}
            {mapSprayWalls.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px" }}>
                <svg width="40" height="40" viewBox="0 0 40 40" style={{ flexShrink: 0 }}>
                  <g transform="translate(20, 20)"><SprayWallMarkerGlyph /></g>
                </svg>
                <span style={{ fontSize: 12, color: th.textMuted }}>{t('map.legendSprayWall')}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 5. MAP */}
      <div style={{ padding: "0 12px" }}>
        <ZoomableMap
          mapWidth={currentMap?.width || 520} mapHeight={currentMap?.height || 480}
          mapImageUrl={resolveMapImageUrl(currentMap, gymMeta?.slug)} mapBgColor={currentMap?.bgColor || null}
          routes={filteredRoutes} doneSet={doneSet} compToppedRouteIds={compToppedRouteIds}
          onRouteClick={handleRouteTap} onRouteLongPress={onRouteLongPress} onMapClick={handleMapClickOrReposition} isPlacing={!!pending} repositionId={pending?._editId}
          sprayWalls={mapSprayWalls} onSprayWallClick={onSprayWallClick}
          placingSprayWallId={placingWall ? pending.wallId : null}
          difficulty={diff} holdColors={holdC} searchQuery={mapSearch}
          newRouteDays={resolveNewRouteDays(settings)} today={today}
          persistKey={gymMeta?.id && currentMap?.id ? `${gymMeta.id}-${currentMap.id}` : null}
          focusRequest={mapFocusRequest}
          compRouteIds={compRouteIds}
          quickLogActive={!!quickLog?.active}
          quickLogCounts={quickLogCounts}
          onToggleQuickLog={quickLog?.toggle}
          archiveActive={!!archiveMode?.active}
          // Only offered to a setter who may actually archive; the hook re-checks
          // the capability on every tap, so a role change mid-session cannot
          // leave a live handler behind a stale render.
          onToggleArchive={canArchive ? () => {
            quickLog?.stop?.();
            if (!archiveMode?.active) logEvent(EVT.MAP_ARCHIVE_MODE, { gym_id: gymScope, action: "start" });
            archiveMode?.toggle?.();
          } : null}
          onOpenFilters={() => setShowMapFilters(true)}
          activeFilterCount={activeFilterCount}
        />
      </div>

      {/* 6. ROUTE LIST */}
      <div style={{ padding: "0 12px" }}>
        {/* List header */}
        <div style={{ padding: "8px 0", fontSize: 14, fontWeight: 800, color: th.text }}>{t('map.routes')} ({sortedRouteList.length})</div>

        {/* Route items */}
        <div>
          {sortedRouteList.length === 0 && (
            <EmptyState size="lg">
              <div style={{ fontSize: 32, marginBottom: 8 }}>🧐</div>
              {/* Two different empty states: this map simply has no routes yet,
                  or a filter/search hid them all. One sentence for both would
                  blame the filters even when the user has none switched on —
                  which is also why the clear-filters button below stays hidden. */}
              <div style={{ marginBottom: 10 }}>{(activeFilterCount > 0 || mapSearch) ? t('map.noResults') : t('map.noRoutesOnMap')}</div>
              {activeFilterCount > 0 && (
                <button onClick={() => {
                  setFilters(f => ({ ...f, status: "all", difficulty: [], compOnly: false }));
                  if (isMixedGym(gymMeta) && typeof setDisciplineFilter === "function") setDisciplineFilter("all");
                }} style={{
                  padding: "8px 18px", borderRadius: 10, border: "1px solid " + accent,
                  background: "transparent", color: accent, fontSize: 13, fontWeight: 700,
                  cursor: "pointer", fontFamily: F,
                }}>{t('filter.clearAll')}</button>
              )}
            </EmptyState>
          )}
          {sortedRouteList.slice(0, routeListVisible).map(r => {
            const hasD = r.difficulty != null;
            const hasH = r.holdColor != null;
            const d = hasD ? (diff[r.difficulty] || { color: "#ccc", border: "#999", label: "?", points: 0, tc: "#333" }) : { color: "#ccc", border: "#999", label: "—", points: 0, tc: "#333" };
            const h = hasH ? (holdC[r.holdColor] || { color: "#ddd", label: "?" }) : { color: "#ddd", label: "—" };
            // "Climbed" has two halves during a competition: a gym ascent, and
            // a top on the viewer's own scorecard — which writes no ascent doc
            // and so never lands in `doneSet`. The row draws one tick for
            // either; see the marker in ZoomableMap for the same rule.
            const ok = doneSet.has(r.id) || !!compToppedRouteIds?.has(r.id);
            const avg = avgRatingMap.get(r.id) || 0;
            // No point number on a competition route — the gym's difficulty
            // points are not what the competition pays, and its own value
            // (fixed redpoint points, or a live pool share) belongs on the
            // scorecard, not here. Same rule as the map marker.
            const isCompRoute = !!(compRouteIds && compRouteIds.has(r.id));
            return (
              <div key={r.id} data-testid="route-row" data-route-id={r.id} onClick={() => setSelRoute(r)} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "10px 8px", borderRadius: 12, cursor: "pointer",
                background: ok ? (accent + "25") : "transparent", marginBottom: 2,
              }}>
                {/* Mini marker — matches large map marker design */}
                <svg width="36" height="36" viewBox="-2 -2 36 36" style={{ flexShrink: 0 }}>
                  {!hasH && (
                    <defs><pattern id={"hatch-mini-" + r.id} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                      <line x1="0" y1="0" x2="0" y2="4" stroke={accent} strokeWidth="1" strokeOpacity="0.5" />
                    </pattern></defs>
                  )}
                  {/* Outer circle: holdColor solid or hatched accent */}
                  <circle cx="16" cy="16" r="14" fill={hasH ? h.color : ("url(#hatch-mini-" + r.id + ")")} stroke={hasH ? "none" : accent} strokeWidth={hasH ? "0" : "1.5"} opacity="0.9" />
                  {/* Inner difficulty circle + points (when hasD) */}
                  {hasD && (
                    <>
                      <circle cx="16" cy="16" r="7" fill={d.color} stroke={d.border} strokeWidth="2" />
                      {!isCompRoute && (
                        <text x="16" y="16.5" textAnchor="middle" dominantBaseline="middle" fill={d.tc} fontSize="7" fontWeight="700" fontFamily="'Space Grotesk',sans-serif">{formatPoints(d.points, lang, true)}p</text>
                      )}
                    </>
                  )}
                  {/* "?" when no difficulty */}
                  {!hasD && (
                    <text x="16" y="16.5" textAnchor="middle" dominantBaseline="middle" fill={hasH ? "#fff" : accent} fontSize="11" fontWeight="700" fontFamily="'Space Grotesk',sans-serif">?</text>
                  )}
                  {/* Climbed badge */}
                  {ok && (
                    <g>
                      <circle cx="27" cy="5" r="6" fill={accent} stroke="#fff" strokeWidth="1.5" />
                      <polyline points="24.5,5.5 26.5,7.5 30,3.5" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                  )}
                </svg>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: th.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", ...(!r.name || !r.name.trim() ? { fontStyle: "italic", opacity: 0.75 } : {}), ...SELECTABLE }}>{getRouteDisplayName(r, diff, holdC, t)}</div>
                  <div style={{ fontSize: 12, color: th.textMuted, ...SELECTABLE }}>
                    {hasD ? tDiffLabel(t, r.difficulty, d.label) : t('route.ungraded')}{getSetterDisplayName(r) ? " · " + getSetterDisplayName(r) : ""}
                    {avg > 0 && <span style={{ marginLeft: 6 }}><span style={{ color: "#f59e0b" }}>★</span> {avg.toFixed(1)}</span>}
                  </div>
                </div>
                {/* Points */}
                {hasD && !isCompRoute && <div style={{ fontSize: 13, fontWeight: 700, color: th.text, flexShrink: 0 }}>{formatPoints(d.points, lang)}p</div>}
                {/* Show-on-map target — same affordance as the routes
                    tab list so users get one consistent way to find
                    routes on the wall. Competition routes get it too: this
                    list is `filteredRoutes`, which already hides the ones
                    the map hides, so anything standing here HAS a marker to
                    jump to — and a competitor mid-session wants to find
                    their boulder on the wall more than anyone.
                    stopPropagation prevents the row's onClick (which
                    opens the route modal) from firing too. */}
                {onShowOnMap && r.active && (
                  <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
                    <IconBtn icon={IconTarget} tone="muted" title={t('routes.showOnMap')} ariaLabel={t('routes.showOnMap')} size={18} onClick={() => onShowOnMap(r)} />
                  </div>
                )}
              </div>
            );
          })}
          {sortedRouteList.length > routeListVisible && (
            <ShowMoreBtn onClick={() => setRouteListVisible(v => v + ROUTE_LIST_PREVIEW)}>
              {t('common.showMore', { count: sortedRouteList.length - routeListVisible })}
            </ShowMoreBtn>
          )}
        </div>
      </div>

      {/* 7. Quick-log bar — occupies the FAB's slot on purpose (see below). */}
      {quickLog?.active && (
        <QuickLogBar
          todayClimbLog={todayClimbLog}
          lastAction={quickLog.lastAction}
          cooldownHit={quickLog.cooldownHit}
          onUndoLast={quickLog.undoLast}
          onStop={quickLog.stop}
          onRemoveClimb={(routeId) => quickLog.removeLastForRoute(todayClimbLog, routeId)}
          routes={routes}
          difficulty={diff}
        />
      )}

      {/* 7b. Archive-mode banner — fixed to the TOP because the bottom belongs to
          the quick-log bar and the FAB, and above the fullscreen map (50) so it
          is visible exactly where the mode can be armed. A destructive tap mode
          must never be on without saying so on screen. */}
      {archiveMode?.active && (
        <ViewportLayer anchor="top" zIndex={60} padding="calc(env(safe-area-inset-top, 0px) + 12px) 12px 0">
        <div style={{
          pointerEvents: "auto", maxWidth: "100%",
          display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
          borderRadius: 12, background: "#dc2626", color: "#fff", fontFamily: F,
          boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
        }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>
            {archiveMode.archived.length > 0
              ? t('archiveMode.armedCount', { count: archiveMode.archived.length })
              : t('archiveMode.armed')}
          </span>
          <button onClick={archiveMode.stop} style={{
            border: "1px solid rgba(255,255,255,0.6)", background: "transparent", color: "#fff",
            borderRadius: 999, padding: "4px 12px", fontSize: 12, fontWeight: 700,
            cursor: "pointer", fontFamily: F, flexShrink: 0,
          }}>{t('archiveMode.done')}</button>
        </div>
        </ViewportLayer>
      )}

      {/* 8. FAB - New route button (setters only), z-index 60 to stay above fullscreen map (50).
          Hidden while quick-log is armed: it sits in exactly this spot, and the
          two are opposite intents anyway — a setter logging their warm-up lap is
          not simultaneously placing routes. Placing a route also ends archive
          mode: adding and stripping are opposite intents, and the builder covers
          the map anyway. */}
      {canCreateRoute && !pending && !quickLog?.active && (
        <NewRouteFab label={t('map.newRoute')} onClick={() => { archiveMode?.stop?.(); setShowBuilder(true); }} />
      )}
    </div>
  );
});
